import React, { useState, createElement, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SendIcon, SettingsIcon, HistoryIcon, CopyIcon, ThumbsUpIcon, ThumbsDownIcon, RotateCcwIcon, XIcon, Trash2Icon, PlusIcon, MoreHorizontalIcon, ShareIcon, DownloadIcon, ChevronLeftIcon, TwitterIcon, FacebookIcon, LinkedinIcon, LinkIcon, UploadIcon, ChefHatIcon, UtensilsIcon } from 'lucide-react';
import { mockApps } from '../utils/mockData';
import { getIcon } from '../utils/iconUtils';
type Message = {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};
type HistoryItem = {
  id: string;
  prompt: string;
  timestamp: Date;
};
type CookingPreferences = {
  dietaryRestrictions: string[];
  allergies: string[];
  measurementSystem: 'metric' | 'imperial';
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  availableEquipment: string[];
  cuisinePreferences: string[];
  cookingTime: string;
  servingSize: number;
};
type AppTab = {
  id: string;
  appId: string;
  messages: Message[];
  history: HistoryItem[];
  prompt: string;
  cookingPreferences?: CookingPreferences;
};
export const AppInterface = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const app = mockApps.find(a => a.id === id);
  // Tab management
  const [tabs, setTabs] = useState<AppTab[]>([{
    id: '1',
    appId: id || '',
    messages: [],
    history: [],
    prompt: '',
    cookingPreferences: {
      dietaryRestrictions: [],
      allergies: [],
      measurementSystem: 'imperial',
      skillLevel: 'intermediate',
      availableEquipment: [],
      cuisinePreferences: [],
      cookingTime: 'any',
      servingSize: 4
    }
  }]);
  const [activeTabId, setActiveTabId] = useState('1');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showTabMenu, setShowTabMenu] = useState(false);
  const [showAppPicker, setShowAppPicker] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareContent, setShareContent] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showCookingPreferences, setShowCookingPreferences] = useState(false);
  // Settings state
  const [settings, setSettings] = useState({
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 2000,
    topP: 1
  });
  const activeTab = tabs.find(t => t.id === activeTabId);
  // Dietary restrictions options
  const dietaryOptions = ['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Keto', 'Paleo', 'Low-Carb', 'Low-Fat', 'Halal', 'Kosher'];
  const equipmentOptions = ['Oven', 'Stovetop', 'Microwave', 'Air Fryer', 'Slow Cooker', 'Instant Pot', 'Blender', 'Food Processor', 'Stand Mixer', 'Grill', 'Cast Iron Skillet', 'Non-Stick Pan', 'Wok', 'Dutch Oven'];
  const cuisineOptions = ['Italian', 'Mexican', 'Chinese', 'Japanese', 'Indian', 'Thai', 'Mediterranean', 'French', 'American', 'Korean'];
  const updateCookingPreferences = (preferences: Partial<CookingPreferences>) => {
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      cookingPreferences: {
        ...t.cookingPreferences!,
        ...preferences
      }
    } : t));
  };
  const toggleArrayItem = (array: string[], item: string, field: keyof CookingPreferences) => {
    const newArray = array.includes(item) ? array.filter(i => i !== item) : [...array, item];
    updateCookingPreferences({
      [field]: newArray
    });
  };
  const openAppInNewTab = (appId: string) => {
    const existingTab = tabs.find(t => t.appId === appId);
    if (existingTab) {
      setActiveTabId(existingTab.id);
      setShowAppPicker(false);
      return;
    }
    const newTab: AppTab = {
      id: Date.now().toString(),
      appId: appId,
      messages: [],
      history: [],
      prompt: '',
      cookingPreferences: appId === 'cooking-helper' ? {
        dietaryRestrictions: [],
        allergies: [],
        measurementSystem: 'imperial',
        skillLevel: 'intermediate',
        availableEquipment: [],
        cuisinePreferences: [],
        cookingTime: 'any',
        servingSize: 4
      } : undefined
    };
    setTabs([...tabs, newTab]);
    setActiveTabId(newTab.id);
    setShowAppPicker(false);
    navigate(`/app-interface/${appId}`);
  };
  const closeTab = (tabId: string) => {
    const newTabs = tabs.filter(t => t.id !== tabId);
    if (newTabs.length === 0) {
      navigate('/my-apps');
      return;
    }
    setTabs(newTabs);
    if (activeTabId === tabId) {
      setActiveTabId(newTabs[0].id);
      const newActiveTab = newTabs[0];
      navigate(`/app-interface/${newActiveTab.appId}`);
    }
  };
  const goBackToMyApps = () => {
    navigate('/my-apps');
  };
  const updateTabPrompt = (prompt: string) => {
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      prompt
    } : t));
  };
  const buildContextualPrompt = (userPrompt: string): string => {
    if (app?.id !== 'cooking-helper' || !activeTab?.cookingPreferences) {
      return userPrompt;
    }
    const prefs = activeTab.cookingPreferences;
    let contextParts: string[] = [];
    if (prefs.dietaryRestrictions.length > 0) {
      contextParts.push(`Dietary restrictions: ${prefs.dietaryRestrictions.join(', ')}`);
    }
    if (prefs.allergies.length > 0) {
      contextParts.push(`Allergies: ${prefs.allergies.join(', ')}`);
    }
    if (prefs.availableEquipment.length > 0) {
      contextParts.push(`Available equipment: ${prefs.availableEquipment.join(', ')}`);
    }
    if (prefs.cuisinePreferences.length > 0) {
      contextParts.push(`Preferred cuisines: ${prefs.cuisinePreferences.join(', ')}`);
    }
    contextParts.push(`Measurement system: ${prefs.measurementSystem}`, `Skill level: ${prefs.skillLevel}`, `Serving size: ${prefs.servingSize} people`);
    if (prefs.cookingTime !== 'any') {
      contextParts.push(`Time available: ${prefs.cookingTime}`);
    }
    const context = contextParts.join('. ');
    return `${context}.\n\nUser request: ${userPrompt}`;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTab?.prompt.trim() || isGenerating) return;
    const contextualPrompt = buildContextualPrompt(activeTab.prompt);
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: activeTab.prompt,
      timestamp: new Date()
    };
    const newHistory: HistoryItem = {
      id: Date.now().toString(),
      prompt: activeTab.prompt,
      timestamp: new Date()
    };
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      messages: [...t.messages, userMessage],
      history: [newHistory, ...t.history],
      prompt: ''
    } : t));
    setIsGenerating(true);
    setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'This is a simulated response. In a real implementation, this would be the actual AI-generated content based on your prompt and cooking preferences.',
        timestamp: new Date()
      };
      setTabs(prevTabs => prevTabs.map(t => t.id === activeTabId ? {
        ...t,
        messages: [...t.messages, assistantMessage]
      } : t));
      setIsGenerating(false);
    }, 2000);
  };
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
  };
  const handleShare = (content: string) => {
    setShareContent(content);
    setShowShareModal(true);
  };
  const handleDownload = (content: string) => {
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `response-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleShareTo = (platform: string) => {
    const text = encodeURIComponent(shareContent);
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareContent);
        break;
    }
    setShowShareModal(false);
  };
  const handleClearHistory = () => {
    setTabs(tabs.map(t => t.id === activeTabId ? {
      ...t,
      messages: []
    } : t));
  };
  const loadHistoryItem = (item: HistoryItem) => {
    updateTabPrompt(item.prompt);
    setShowHistory(false);
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };
  if (!app) {
    return <div className="flex items-center justify-center h-screen bg-gray-50" data-id="element-618">
        <div className="text-center" data-id="element-619">
          <p className="text-gray-500 mb-4" data-id="element-620">App not found</p>
          <button onClick={() => navigate('/my-apps')} className="text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-621">
            Return to My Apps
          </button>
        </div>
      </div>;
  }
  const getWelcomeContent = () => {
    switch (app.id) {
      case 'cooking-helper':
        return {
          emoji: '👨‍🍳',
          examples: ['What can I make with chicken, rice, and vegetables?', 'Give me a quick 30-minute dinner recipe', 'How do I make the perfect pasta carbonara?']
        };
      case 'ghiblifi':
        return {
          emoji: '🎨',
          examples: ['Transform my garden into a Ghibli scene', 'Create a whimsical forest path in Ghibli style', 'Make a cozy cottage with Ghibli aesthetics']
        };
      case 'eli5-explanations':
        return {
          emoji: '🧠',
          examples: ["Explain quantum computing like I'm five", 'How does the internet work in simple terms?', 'What is cryptocurrency in easy words?']
        };
      default:
        return {
          emoji: '💡',
          examples: ['Create an image of a sunset over mountains', 'Generate a creative story about space travel', 'Help me write a professional email']
        };
    }
  };
  const welcomeContent = getWelcomeContent();
  return <div className="h-screen flex flex-col bg-gray-50" data-id="element-622">
      {/* Unified Header with Tabs */}
      <div className="bg-white border-b border-gray-200 shadow-sm" data-id="element-623">
        <div className="flex items-center h-16 px-4" data-id="element-624">
          {/* Left: Back Button */}
          <button onClick={goBackToMyApps} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-indigo-600 mr-3" aria-label="Back to My Apps" title="Back to My Apps" data-id="element-625">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-626" />
          </button>
          {/* Center: Tabs - Desktop */}
          <div className="hidden md:flex flex-1 items-center space-x-2 overflow-x-auto px-2" data-id="element-627">
            {tabs.map(tab => {
            const tabApp = mockApps.find(a => a.id === tab.appId);
            if (!tabApp) return null;
            return <button key={tab.id} onClick={() => {
              setActiveTabId(tab.id);
              navigate(`/app-interface/${tab.appId}`);
            }} className={`group flex items-center space-x-2.5 px-4 py-2.5 rounded-lg transition-all min-w-[180px] ${activeTabId === tab.id ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`} data-id="element-628">
                  <div className={`w-8 h-8 ${tabApp.iconBg} rounded-lg flex items-center justify-center flex-shrink-0 ${activeTabId === tab.id ? 'bg-white/20' : ''}`} data-id="element-629">
                    {getIcon(tabApp.icon)}
                  </div>
                  <span className="font-medium truncate flex-1 text-left" data-id="element-630">
                    {tabApp.title}
                  </span>
                  <button onClick={e => {
                e.stopPropagation();
                closeTab(tab.id);
              }} className={`p-1 rounded transition-colors ${activeTabId === tab.id ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`} aria-label="Close tab" data-id="element-631">
                    <XIcon className="h-4 w-4" data-id="element-632" />
                  </button>
                </button>;
          })}
            <button onClick={() => setShowAppPicker(true)} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 flex-shrink-0" aria-label="Open new app" title="Open new app" data-id="element-633">
              <PlusIcon className="h-5 w-5" data-id="element-634" />
            </button>
          </div>
          {/* Center: Mobile Tab Info */}
          <div className="md:hidden flex-1 flex items-center justify-center" data-id="element-635">
            <div className="flex items-center space-x-2" data-id="element-636">
              <div className={`w-8 h-8 ${app.iconBg} rounded-lg flex items-center justify-center`} data-id="element-637">
                {getIcon(app.icon)}
              </div>
              <div className="text-left" data-id="element-638">
                <h1 className="font-semibold text-sm text-gray-900 truncate max-w-[150px]" data-id="element-639">
                  {app.title}
                </h1>
                <p className="text-xs text-gray-500" data-id="element-640">
                  Tab {tabs.findIndex(t => t.id === activeTabId) + 1} of{' '}
                  {tabs.length}
                </p>
              </div>
            </div>
          </div>
          {/* Right: Controls */}
          <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0" data-id="element-641">
            {/* Cooking Preferences Button - Only for Cooking Helper */}
            {app.id === 'cooking-helper' && <button onClick={() => setShowCookingPreferences(true)} className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${showCookingPreferences ? 'bg-orange-100 text-orange-600' : 'text-gray-600'}`} aria-label="Cooking Preferences" title="Cooking Preferences" data-id="element-642">
                <ChefHatIcon className="h-5 w-5" data-id="element-643" />
              </button>}
            {/* Mobile: Tab Menu */}
            <div className="md:hidden flex items-center space-x-1" data-id="element-644">
              <button onClick={() => setShowAppPicker(true)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Open new app" data-id="element-645">
                <PlusIcon className="h-5 w-5 text-gray-600" data-id="element-646" />
              </button>
              {tabs.length > 1 && <button onClick={() => setShowTabMenu(!showTabMenu)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Switch tabs" data-id="element-647">
                  <MoreHorizontalIcon className="h-5 w-5 text-gray-600" data-id="element-648" />
                </button>}
            </div>
            <button onClick={() => setShowHistory(!showHistory)} className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${showHistory ? 'bg-gray-100 text-indigo-600' : 'text-gray-600'}`} aria-label="History" title="History" data-id="element-649">
              <HistoryIcon className="h-5 w-5" data-id="element-650" />
            </button>
            <button onClick={() => setShowSettings(!showSettings)} className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${showSettings ? 'bg-gray-100 text-indigo-600' : 'text-gray-600'}`} aria-label="Settings" title="Settings" data-id="element-651">
              <SettingsIcon className="h-5 w-5" data-id="element-652" />
            </button>
          </div>
        </div>
        {/* Mobile Tab Menu */}
        {showTabMenu && <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50 max-h-64 overflow-y-auto" data-id="element-653">
            {tabs.map(tab => {
          const tabApp = mockApps.find(a => a.id === tab.appId);
          if (!tabApp) return null;
          return <button key={tab.id} onClick={() => {
            setActiveTabId(tab.id);
            navigate(`/app-interface/${tab.appId}`);
            setShowTabMenu(false);
          }} className={`w-full flex items-center space-x-3 px-4 py-3 border-b border-gray-100 ${activeTabId === tab.id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`} data-id="element-654">
                  <div className={`w-10 h-10 ${tabApp.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`} data-id="element-655">
                    {getIcon(tabApp.icon)}
                  </div>
                  <span className="font-medium flex-1 text-left" data-id="element-656">
                    {tabApp.title}
                  </span>
                  <button onClick={e => {
              e.stopPropagation();
              closeTab(tab.id);
              if (tabs.length === 1) setShowTabMenu(false);
            }} className="p-1.5 hover:bg-gray-200 rounded" aria-label="Close tab" data-id="element-657">
                    <XIcon className="h-4 w-4 text-gray-500" data-id="element-658" />
                  </button>
                </button>;
        })}
          </div>}
      </div>
      <div className="flex-1 flex overflow-hidden" data-id="element-659">
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col bg-white m-3 md:m-4 rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-660">
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-id="element-661">
            {!activeTab?.messages.length ? <div className="h-full flex items-center justify-center" data-id="element-662">
                <div className="text-center max-w-md px-4" data-id="element-663">
                  <div className={`${app.iconBg} w-20 h-20 md:w-24 md:h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl`} data-id="element-664">
                    <div className="scale-150" data-id="element-665">{getIcon(app.icon)}</div>
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold mb-3 text-gray-900" data-id="element-666">
                    Welcome to {app.title}
                  </h2>
                  <p className="text-sm md:text-base text-gray-600 mb-8" data-id="element-667">
                    {app.description}
                  </p>
                  {app.id === 'cooking-helper' && <button onClick={() => setShowCookingPreferences(true)} className="mb-6 inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all shadow-md" data-id="element-668">
                      <ChefHatIcon className="h-5 w-5 mr-2" data-id="element-669" />
                      Set Your Cooking Preferences
                    </button>}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-5 md:p-6 text-left" data-id="element-670">
                    <p className="text-sm font-semibold text-indigo-900 mb-4 flex items-center" data-id="element-671">
                      <span className="text-2xl mr-2" data-id="element-672">
                        {welcomeContent.emoji}
                      </span>
                      Try asking:
                    </p>
                    <ul className="text-sm text-indigo-700 space-y-3" data-id="element-673">
                      {welcomeContent.examples.map((example, index) => <li key={index} className="flex items-start" data-id="element-674">
                          <span className="mr-2 mt-0.5" data-id="element-675">•</span>
                          <span data-id="element-676">{example}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div> : <>
                {activeTab.messages.map(message => <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-677">
                    <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl p-4 md:p-5 shadow-md ${message.type === 'user' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' : 'bg-gray-50 border border-gray-200'}`} data-id="element-678">
                      <div className="flex items-center justify-between mb-2" data-id="element-679">
                        <div className="flex items-center space-x-2" data-id="element-680">
                          {message.type === 'assistant' && <div className={`w-6 h-6 ${app.iconBg} rounded-lg flex items-center justify-center`} data-id="element-681">
                              <div className="scale-75" data-id="element-682">
                                {getIcon(app.icon)}
                              </div>
                            </div>}
                          <span className="text-xs font-semibold opacity-80" data-id="element-683">
                            {message.type === 'user' ? 'You' : app.title}
                          </span>
                        </div>
                        <span className="text-xs opacity-60" data-id="element-684">
                          {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                        </span>
                      </div>
                      <p className="text-sm md:text-base whitespace-pre-wrap leading-relaxed" data-id="element-685">
                        {message.content}
                      </p>
                      {message.type === 'assistant' && <div className="flex items-center space-x-1 mt-4 pt-3 border-t border-gray-200" data-id="element-686">
                          <button onClick={() => handleCopy(message.content)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Copy" data-id="element-687">
                            <CopyIcon className="h-4 w-4 text-gray-600" data-id="element-688" />
                          </button>
                          <button onClick={() => handleShare(message.content)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Share" data-id="element-689">
                            <ShareIcon className="h-4 w-4 text-gray-600" data-id="element-690" />
                          </button>
                          <button onClick={() => handleDownload(message.content)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Download" data-id="element-691">
                            <DownloadIcon className="h-4 w-4 text-gray-600" data-id="element-692" />
                          </button>
                          <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Good response" data-id="element-693">
                            <ThumbsUpIcon className="h-4 w-4 text-gray-600" data-id="element-694" />
                          </button>
                          <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Bad response" data-id="element-695">
                            <ThumbsDownIcon className="h-4 w-4 text-gray-600" data-id="element-696" />
                          </button>
                          <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors" title="Regenerate" data-id="element-697">
                            <RotateCcwIcon className="h-4 w-4 text-gray-600" data-id="element-698" />
                          </button>
                        </div>}
                    </div>
                  </div>)}
                {isGenerating && <div className="flex justify-start" data-id="element-699">
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 max-w-[85%] md:max-w-[75%] shadow-md" data-id="element-700">
                      <div className="flex items-center space-x-3" data-id="element-701">
                        <div className="flex space-x-1.5" data-id="element-702">
                          <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-bounce" data-id="element-703" />
                          <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.2s]" data-id="element-704" />
                          <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.4s]" data-id="element-705" />
                        </div>
                        <span className="text-sm text-gray-600 font-medium" data-id="element-706">
                          Generating response...
                        </span>
                      </div>
                    </div>
                  </div>}
              </>}
          </div>
          {/* Input Area */}
          <div className="border-t border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4 md:p-5" data-id="element-707">
            <form onSubmit={handleSubmit} className="max-w-4xl mx-auto" data-id="element-708">
              {/* Image Preview */}
              {imagePreview && <div className="mb-3 relative inline-block" data-id="element-709">
                  <img src={imagePreview} alt="Upload preview" className="h-20 w-20 object-cover rounded-lg border-2 border-indigo-500" data-id="element-710" />
                  <button type="button" onClick={removeImage} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors" data-id="element-711">
                    <XIcon className="h-3 w-3" data-id="element-712" />
                  </button>
                </div>}
              <div className="flex items-start gap-3" data-id="element-713">
                <div className="flex-1 relative" data-id="element-714">
                  <textarea value={activeTab?.prompt || ''} onChange={e => updateTabPrompt(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }} placeholder="Type your message..." className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm md:text-base shadow-sm" rows={2} disabled={isGenerating} data-id="element-715" />
                  {activeTab && activeTab.messages.length > 0 && <button type="button" onClick={handleClearHistory} className="absolute right-3 top-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Clear conversation" data-id="element-716">
                      <Trash2Icon className="h-4 w-4" data-id="element-717" />
                    </button>}
                </div>
                {/* Upload Button - Show for apps that allow image upload */}
                {(app.id === 'ghiblifi' || app.id === 'image-wizard-pro' || app.id === 'art-generator') && <label className="cursor-pointer bg-gray-100 text-gray-700 p-3 rounded-xl hover:bg-gray-200 transition-all shadow-sm flex-shrink-0" data-id="element-718">
                    <UploadIcon className="h-5 w-5" data-id="element-719" />
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-id="element-720" />
                  </label>}
                <button type="submit" disabled={!activeTab?.prompt.trim() || isGenerating} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-3 rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex-shrink-0" aria-label="Send message" data-id="element-721">
                  <SendIcon className="h-5 w-5" data-id="element-722" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-gray-500" data-id="element-723">
                <span className="hidden md:inline" data-id="element-724">
                  Press Enter to send, Shift + Enter for new line
                </span>
                <span className="md:hidden" data-id="element-725">Tap send or press Enter</span>
                <span className="font-medium" data-id="element-726">
                  {activeTab?.prompt.length || 0} characters
                </span>
              </div>
            </form>
          </div>
        </div>
        {/* Settings Panel */}
        {showSettings && <div className="w-full md:w-80 absolute md:relative top-0 bottom-0 right-0 md:border-l border-gray-200 bg-white overflow-y-auto z-40 shadow-xl md:shadow-none" data-id="element-727">
            <div className="p-6" data-id="element-728">
              <div className="flex items-center justify-between mb-6" data-id="element-729">
                <h3 className="font-bold text-lg" data-id="element-730">Settings</h3>
                <button onClick={() => setShowSettings(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close settings" data-id="element-731">
                  <XIcon className="h-5 w-5 text-gray-600" data-id="element-732" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-733">
                <div data-id="element-734">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-735">
                    AI Model
                  </label>
                  <select value={settings.model} onChange={e => setSettings({
                ...settings,
                model: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-736">
                    <option value="gpt-4" data-id="element-737">GPT-4</option>
                    <option value="gpt-3.5" data-id="element-738">GPT-3.5 Turbo</option>
                    <option value="claude" data-id="element-739">Claude 2</option>
                    <option value="gemini" data-id="element-740">Gemini Pro</option>
                  </select>
                </div>
                <div data-id="element-741">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-742">
                    Temperature: {settings.temperature}
                  </label>
                  <input type="range" min="0" max="2" step="0.1" value={settings.temperature} onChange={e => setSettings({
                ...settings,
                temperature: parseFloat(e.target.value)
              })} className="w-full" data-id="element-743" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-744">
                    <span data-id="element-745">Precise</span>
                    <span data-id="element-746">Creative</span>
                  </div>
                </div>
                <div data-id="element-747">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-748">
                    Max Tokens: {settings.maxTokens}
                  </label>
                  <input type="range" min="100" max="4000" step="100" value={settings.maxTokens} onChange={e => setSettings({
                ...settings,
                maxTokens: parseInt(e.target.value)
              })} className="w-full" data-id="element-749" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-750">
                    <span data-id="element-751">Short</span>
                    <span data-id="element-752">Long</span>
                  </div>
                </div>
                <div data-id="element-753">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-754">
                    Top P: {settings.topP}
                  </label>
                  <input type="range" min="0" max="1" step="0.1" value={settings.topP} onChange={e => setSettings({
                ...settings,
                topP: parseFloat(e.target.value)
              })} className="w-full" data-id="element-755" />
                </div>
                <div className="pt-4 border-t border-gray-200" data-id="element-756">
                  <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-757">
                    Quick Actions
                  </h4>
                  <div className="space-y-2" data-id="element-758">
                    <button className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-759">
                      Export Conversation
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-760">
                      View App Details
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" data-id="element-761">
                      Reset Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* History Panel */}
        {showHistory && <div className="w-full md:w-80 absolute md:relative top-0 bottom-0 right-0 md:border-l border-gray-200 bg-white overflow-y-auto z-40 shadow-xl md:shadow-none" data-id="element-762">
            <div className="p-6" data-id="element-763">
              <div className="flex items-center justify-between mb-6" data-id="element-764">
                <h3 className="font-bold text-lg" data-id="element-765">History</h3>
                <button onClick={() => setShowHistory(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close history" data-id="element-766">
                  <XIcon className="h-5 w-5 text-gray-600" data-id="element-767" />
                </button>
              </div>
              {!activeTab?.history.length ? <div className="text-center py-12" data-id="element-768">
                  <HistoryIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" data-id="element-769" />
                  <p className="text-sm text-gray-500" data-id="element-770">No history yet</p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-771">
                    Your prompts will appear here
                  </p>
                </div> : <div className="space-y-2" data-id="element-772">
                  {activeTab.history.map(item => <button key={item.id} onClick={() => loadHistoryItem(item)} className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200" data-id="element-773">
                      <p className="text-sm text-gray-900 line-clamp-2 mb-1.5" data-id="element-774">
                        {item.prompt}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-775">
                        {item.timestamp.toLocaleString()}
                      </p>
                    </button>)}
                </div>}
            </div>
          </div>}
      </div>
      {/* Cooking Preferences Modal */}
      {showCookingPreferences && app.id === 'cooking-helper' && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-776">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden" data-id="element-777">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500" data-id="element-778">
              <div className="flex items-center space-x-3" data-id="element-779">
                <ChefHatIcon className="h-7 w-7 text-white" data-id="element-780" />
                <h2 className="text-2xl font-bold text-white" data-id="element-781">
                  Cooking Preferences
                </h2>
              </div>
              <button onClick={() => setShowCookingPreferences(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors" aria-label="Close" data-id="element-782">
                <XIcon className="h-6 w-6 text-white" data-id="element-783" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-88px)]" data-id="element-784">
              <div className="space-y-8" data-id="element-785">
                {/* Measurement System */}
                <div data-id="element-786">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-787">
                    Measurement System
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-788">
                    <button onClick={() => updateCookingPreferences({
                  measurementSystem: 'metric'
                })} className={`p-4 rounded-xl border-2 transition-all ${activeTab?.cookingPreferences?.measurementSystem === 'metric' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-789">
                      <div className="font-semibold" data-id="element-790">Metric</div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-791">
                        grams, liters, celsius
                      </div>
                    </button>
                    <button onClick={() => updateCookingPreferences({
                  measurementSystem: 'imperial'
                })} className={`p-4 rounded-xl border-2 transition-all ${activeTab?.cookingPreferences?.measurementSystem === 'imperial' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-792">
                      <div className="font-semibold" data-id="element-793">Imperial</div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-794">
                        cups, ounces, fahrenheit
                      </div>
                    </button>
                  </div>
                </div>
                {/* Skill Level */}
                <div data-id="element-795">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-796">
                    Cooking Skill Level
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-797">
                    {['beginner', 'intermediate', 'advanced'].map(level => <button key={level} onClick={() => updateCookingPreferences({
                  skillLevel: level as any
                })} className={`p-3 rounded-xl border-2 transition-all capitalize ${activeTab?.cookingPreferences?.skillLevel === level ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-798">
                        {level}
                      </button>)}
                  </div>
                </div>
                {/* Serving Size */}
                <div data-id="element-799">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-800">
                    Default Serving Size:{' '}
                    {activeTab?.cookingPreferences?.servingSize} people
                  </label>
                  <input type="range" min="1" max="12" value={activeTab?.cookingPreferences?.servingSize || 4} onChange={e => updateCookingPreferences({
                servingSize: parseInt(e.target.value)
              })} className="w-full" data-id="element-801" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-802">
                    <span data-id="element-803">1</span>
                    <span data-id="element-804">12</span>
                  </div>
                </div>
                {/* Cooking Time */}
                <div data-id="element-805">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-806">
                    Preferred Cooking Time
                  </label>
                  <select value={activeTab?.cookingPreferences?.cookingTime || 'any'} onChange={e => updateCookingPreferences({
                cookingTime: e.target.value
              })} className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-id="element-807">
                    <option value="any" data-id="element-808">Any time</option>
                    <option value="15min" data-id="element-809">Under 15 minutes</option>
                    <option value="30min" data-id="element-810">Under 30 minutes</option>
                    <option value="1hour" data-id="element-811">Under 1 hour</option>
                    <option value="2hours" data-id="element-812">1-2 hours</option>
                  </select>
                </div>
                {/* Dietary Restrictions */}
                <div data-id="element-813">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-814">
                    Dietary Restrictions
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-815">
                    {dietaryOptions.map(diet => <button key={diet} onClick={() => toggleArrayItem(activeTab?.cookingPreferences?.dietaryRestrictions || [], diet, 'dietaryRestrictions')} className={`px-3 py-2 rounded-lg text-sm transition-all ${activeTab?.cookingPreferences?.dietaryRestrictions.includes(diet) ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-816">
                        {diet}
                      </button>)}
                  </div>
                </div>
                {/* Allergies */}
                <div data-id="element-817">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-818">
                    Allergies
                  </label>
                  <input type="text" placeholder="Enter allergies separated by commas (e.g., peanuts, shellfish)" value={activeTab?.cookingPreferences?.allergies.join(', ') || ''} onChange={e => {
                const allergies = e.target.value.split(',').map(a => a.trim()).filter(a => a);
                updateCookingPreferences({
                  allergies
                });
              }} className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-id="element-819" />
                </div>
                {/* Available Equipment */}
                <div data-id="element-820">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-821">
                    Available Equipment
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-822">
                    {equipmentOptions.map(equipment => <button key={equipment} onClick={() => toggleArrayItem(activeTab?.cookingPreferences?.availableEquipment || [], equipment, 'availableEquipment')} className={`px-3 py-2 rounded-lg text-sm transition-all ${activeTab?.cookingPreferences?.availableEquipment.includes(equipment) ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-823">
                        {equipment}
                      </button>)}
                  </div>
                </div>
                {/* Cuisine Preferences */}
                <div data-id="element-824">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-825">
                    Favorite Cuisines
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-826">
                    {cuisineOptions.map(cuisine => <button key={cuisine} onClick={() => toggleArrayItem(activeTab?.cookingPreferences?.cuisinePreferences || [], cuisine, 'cuisinePreferences')} className={`px-3 py-2 rounded-lg text-sm transition-all ${activeTab?.cookingPreferences?.cuisinePreferences.includes(cuisine) ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-827">
                        {cuisine}
                      </button>)}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200" data-id="element-828">
                <button onClick={() => setShowCookingPreferences(false)} className="px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium" data-id="element-829">
                  Cancel
                </button>
                <button onClick={() => setShowCookingPreferences(false)} className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all shadow-md font-medium" data-id="element-830">
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* App Picker Modal */}
      {showAppPicker && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-831">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden" data-id="element-832">
            <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-833">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-834">Open an App</h2>
              <button onClick={() => setShowAppPicker(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close" data-id="element-835">
                <XIcon className="h-6 w-6 text-gray-500" data-id="element-836" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-88px)]" data-id="element-837">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-838">
                {mockApps.map(app => <button key={app.id} onClick={() => openAppInNewTab(app.id)} className="flex items-center space-x-4 p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all group" data-id="element-839">
                    <div className={`${app.iconBg} w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-840">
                      {getIcon(app.icon)}
                    </div>
                    <div className="flex-1 text-left min-w-0" data-id="element-841">
                      <h3 className="font-semibold text-gray-900 truncate" data-id="element-842">
                        {app.title}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2" data-id="element-843">
                        {app.description}
                      </p>
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </div>}
      {/* Share Modal */}
      {showShareModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-844">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-id="element-845">
            <div className="flex items-center justify-between mb-6" data-id="element-846">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-847">
                Share Response
              </h3>
              <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close" data-id="element-848">
                <XIcon className="h-5 w-5 text-gray-500" data-id="element-849" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-850">
              <button onClick={() => handleShareTo('twitter')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all" data-id="element-851">
                <TwitterIcon className="h-5 w-5 text-blue-400" data-id="element-852" />
                <span className="font-medium" data-id="element-853">Twitter</span>
              </button>
              <button onClick={() => handleShareTo('facebook')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-600 hover:bg-blue-50 transition-all" data-id="element-854">
                <FacebookIcon className="h-5 w-5 text-blue-600" data-id="element-855" />
                <span className="font-medium" data-id="element-856">Facebook</span>
              </button>
              <button onClick={() => handleShareTo('linkedin')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-700 hover:bg-blue-50 transition-all" data-id="element-857">
                <LinkedinIcon className="h-5 w-5 text-blue-700" data-id="element-858" />
                <span className="font-medium" data-id="element-859">LinkedIn</span>
              </button>
              <button onClick={() => handleShareTo('copy')} className="flex items-center justify-center space-x-2 p-4 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all" data-id="element-860">
                <LinkIcon className="h-5 w-5 text-gray-600" data-id="element-861" />
                <span className="font-medium" data-id="element-862">Copy Link</span>
              </button>
            </div>
          </div>
        </div>}
      {/* Overlay for mobile panels */}
      {(showSettings || showHistory) && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30" onClick={() => {
      setShowSettings(false);
      setShowHistory(false);
    }} data-id="element-863" />}
    </div>;
};